<template>
  <section v-if="shouldRender" class="relative overflow-hidden bg-gradient-to-r from-hilab/10 via-white to-white py-20">
    <div class="absolute inset-y-0 right-0 -z-10 hidden w-1/3 bg-[radial-gradient(circle_at_top,rgba(31,147,255,0.2),transparent)] md:block"></div>
    <div class="mx-auto grid w-full max-w-6xl gap-14 px-4 sm:px-8 lg:px-12 xl:px-[120px] md:grid-cols-[1.1fr_0.9fr] md:items-center">
      <div>
        <span v-if="badge" class="badge">{{ badge }}</span>
        <h2 v-if="heading" class="section-title mt-6">{{ heading }}</h2>
        <p v-if="description" class="section-subtitle">
          {{ description }}
        </p>
        <div v-if="flows.length" class="mt-10 space-y-4">
          <div v-for="flow in flows" :key="flow.title" class="flex gap-4 rounded-2xl border border-slate-100 bg-white/80 p-5 shadow-sm">
            <div class="icon-circle mt-1 h-10 w-10 bg-hilab/10 text-hilab">
              <svg v-if="flow.icon === 'spark'" class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="m12 3 1.8 5.4H19l-4.5 3.2 1.8 5.5L12 14.3 7.7 17.9l1.8-5.5L5 8.4h5.2z" />
              </svg>
              <svg v-else-if="flow.icon === 'router'" class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 4v16m0 0-6-6m6 6 6-6M4 12l8-8 8 8" />
              </svg>
              <svg v-else class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 7h16M4 12h10m-6 5h12" />
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-slate-900">
                {{ flow.title }}
              </h3>
              <p class="mt-2 text-sm leading-relaxed text-slate-500">
                {{ flow.description }}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="relative">
        <div class="absolute -left-6 -right-8 -top-8 -bottom-8 -z-10 rounded-3xl bg-gradient-to-br from-hilab/10 via-white to-white blur-2xl"></div>
        <div class="card-surface gradient-border overflow-hidden">
          <header class="flex items-center justify-between border-b border-slate-100 px-6 py-5">
            <div>
              <p class="text-xs uppercase tracking-[0.3em] text-slate-400">Automation timeline</p>
              <p class="mt-1 text-lg font-semibold text-slate-900">Kịch bản chăm sóc</p>
            </div>
            <span class="rounded-full bg-hilab/10 px-3 py-1 text-xs font-semibold text-hilab">Running</span>
          </header>
          <div v-if="stages.length" class="space-y-4 px-6 py-6 text-sm text-slate-500">
            <div v-for="stage in stages" :key="stage.time || stage.title" class="rounded-2xl border border-slate-100 bg-slate-25/80 p-4">
              <div class="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-slate-400">
                <span>{{ stage.time }}</span>
                <span>{{ stage.channel }}</span>
              </div>
              <p class="mt-3 text-base font-semibold text-slate-900">
                {{ stage.title }}
              </p>
              <p class="mt-2 text-xs text-slate-400">{{ stage.subtitle }}</p>
            </div>
          </div>
          <footer v-if="resultText" class="flex items-center justify-between border-t border-slate-100 bg-slate-25 px-6 py-4">
            <div>
              <p class="text-xs uppercase tracking-[0.2em] text-slate-400">Kết quả</p>
              <p class="mt-1 text-base font-semibold text-slate-900">
                {{ resultText }}
              </p>
            </div>
          </footer>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  content: {
    type: Object,
    default: () => ({}),
  },
});

const toArray = value => {
  if (Array.isArray(value)) return value;
  return value && typeof value === 'object' ? Object.values(value) : [];
};

const sectionContent = computed(() => props.content || {});
const badge = computed(() => sectionContent.value.badge || '');
const heading = computed(() => sectionContent.value.heading || '');
const description = computed(() => sectionContent.value.description || '');
const flows = computed(() => toArray(sectionContent.value.flows).filter(flow => flow?.title || flow?.description));
const stages = computed(() => toArray(sectionContent.value.stages).filter(stage => stage?.title || stage?.subtitle));
const resultText = computed(() => sectionContent.value.result_text || '');
const shouldRender = computed(() => heading.value || description.value || flows.value.length || stages.value.length || resultText.value);
</script>
